// The unit-test harness `init` copies into a project.
//
// Two halves that have to agree: the files under `project-files/` in this package, and what a project
// needs installed to run them. Both are declared here so a change to one is made next to the other.

/** Files shipped inside this package for copying into a project, relative to the package root. */
export const PROJECT_FILES_DIRECTORY = 'project-files'

/** Where the harness lands in the project. Test suites reach it as `require('../../../sandbox')`. */
export const SANDBOX_DIRECTORY = 'sandbox'

/**
 * What `init` copies, as paths relative to both `project-files/` and the project root.
 *
 * An explicit list rather than a directory walk: a file that ships in this package but is not meant to
 * land in a customer's project should not do so by being added to a folder.
 */
export const TESTING_FILES = [
  'jest.config.js',
  'jest.setup.js',
  `${ SANDBOX_DIRECTORY }/index.js`,
  `${ SANDBOX_DIRECTORY }/service.js`,
  `${ SANDBOX_DIRECTORY }/nock-mock.js`,
  `${ SANDBOX_DIRECTORY }/files-mock.js`,
]

/**
 * What the harness needs installed, pinned to the majors it was written against.
 *
 * `query-string` is the sharp one: v8 is ESM-only, so a CommonJS `require` of it fails outright, and
 * `nock-mock.js` depends on its `parseNumbers` / `parseBooleans` options to put JS types back on a
 * query string that carries none. `nock` 14 is the first version that intercepts `fetch` as well as
 * `http`, which is what makes the mock work for a service using a bundled SDK.
 */
export const TEST_DEV_DEPENDENCIES: Record<string, string> = {
  jest          : '^30.0.0',
  nock          : '^14.0.0',
  'query-string': '^7.1.3',
}

/** Added to `scripts`, and only where the project has no script of that name already. */
export const TEST_SCRIPTS: Record<string, string> = {
  test           : 'jest',
  'test:coverage': 'jest --coverage',
}

/**
 * Script values that count as absent — `npm init -y` writes this one, and it is not a decision anybody made.
 *
 * Without it the extremely common `npm init -y` → `flowrunner init` → `npm test` ends at
 * *Error: no test specified*, with a jest config and a sandbox sitting right there unused. Matched on the
 * exact string, so a project that has written its own `test` script keeps it whatever it says.
 */
export const PLACEHOLDER_SCRIPTS: Record<string, string> = {
  test: 'echo "Error: no test specified" && exit 1',
}

/** Both are produced by a test run: coverage reports, and the Files sandbox's blob storage. */
export const TEST_GITIGNORE_ENTRIES = ['coverage/', 'temp/']
